// ───────────────────────────────────────────────────────────────────────────
// Demo data — completely fictitious content used to populate the demo org view.
// Lives in module-level mutable state so light "interactive" edits (e.g. sending
// a chat message, uploading a document) persist for the duration of the tab
// session and reset on a hard reload.
// ───────────────────────────────────────────────────────────────────────────

import { DEMO_ORG_ID, DEMO_USER_ID } from './demoMode';
import { Role, User } from '../types/auth.type';
import { Organization } from '../types/organization.type';
import { ChatMessage, ChatSearchResult } from '../types/chat.type';
import { ReportingTask } from '../types/reportingTask.type';
import { Invoice } from '../types/invoice.type';
import { Report, ReportType, ProcessingStatus, SourceType } from '../types/report.type';
import { OrganizationUser } from '../types/organization-user.type';
import type { MonthClosureItem, MonthClosureStatus, MonthlyClosureResponse } from '../types/monthlyClosure.type';
import type { ClientProjectProgressResponse } from '../types/accountantWorkflow.type';
import type { ResponseTimeItem, ResponseTimeResponse } from '../types/responseTime.type';
import {
  computePaymentMonitoringSummary,
  filterPaymentMonitoringItems,
} from '../utils/paymentMonitoringFilters.utils';

// ── date helpers (relative to "now" so the demo never looks stale) ──────────────
const now = () => new Date();
const iso = (d: Date) => d.toISOString();
const daysAgo = (n: number) => { const d = now(); d.setDate(d.getDate() - n); return d; };
const minsAgo = (n: number) => { const d = now(); d.setMinutes(d.getMinutes() - n); return d; };
const daysFromNow = (n: number) => { const d = now(); d.setDate(d.getDate() + n); return d; };

// Chat timestamps are rendered as local time (server sends UTC without 'Z'),
// so strip the trailing 'Z' to match the GET response format.
const chatTime = (d: Date) => iso(d).replace(/Z$/, '');

let _seq = 1000;
const nextId = (prefix: string) => `${prefix}-${++_seq}`;

// ── Organization ────────────────────────────────────────────────────────────
export const demoOrganization: Organization = {
  id: DEMO_ORG_ID,
  name: 'مؤسسة الأفق التجارية',
  email: 'demo@mohasby.com',
  is_premium: true,
  service_status: 'premium' as const,
  client_tier: 'tier_a',
  sector: 'trade_retail',
  accounting_software: 'Qoyod',
  client_brief: 'مؤسسة تجارية في قطاع التجزئة، تعمل منذ 2019 وتبحث عن محاسب عن بُعد.',
  monthly_invoices_count: '50-100',
  subscription: {
    plan_code: 'remote_accountant_sme',
    billing_cycle: 'annual',
    subscription_started_at: iso(daysAgo(60)),
    subscription_expires_at: iso(daysFromNow(305)),
    subscription_cancelled_at: null,
  },
  terms_accepted_at: null,
  terms_accepted_by_user_id: null,
  data: {
    organization_type: 'company',
    has_regular_accounts: 'yes',
    desired_service: 'remote_accountant',
    reporting_type: 'monthly',
  },
};

export function updateDemoOrganization(data: Partial<Organization>): Organization {
  Object.assign(demoOrganization, {
    name: data.name ?? demoOrganization.name,
    email: data.email ?? demoOrganization.email,
    is_premium: data.is_premium ?? demoOrganization.is_premium,
    service_status: data.service_status ?? demoOrganization.service_status,
    client_tier: data.client_tier !== undefined ? data.client_tier : demoOrganization.client_tier,
    legal_entity_type: data.legal_entity_type !== undefined
      ? data.legal_entity_type
      : demoOrganization.legal_entity_type,
    has_tax_registration: data.has_tax_registration !== undefined
      ? data.has_tax_registration
      : demoOrganization.has_tax_registration,
    sector: data.sector !== undefined ? data.sector : demoOrganization.sector,
    accounting_software: data.accounting_software !== undefined
      ? data.accounting_software
      : demoOrganization.accounting_software,
    client_brief: data.client_brief !== undefined ? data.client_brief : demoOrganization.client_brief,
    monthly_invoices_count: data.monthly_invoices_count !== undefined
      ? data.monthly_invoices_count
      : demoOrganization.monthly_invoices_count,
    terms_accepted_at: data.terms_accepted_at !== undefined
      ? data.terms_accepted_at
      : demoOrganization.terms_accepted_at,
    terms_accepted_by_user_id: data.terms_accepted_by_user_id !== undefined
      ? data.terms_accepted_by_user_id
      : demoOrganization.terms_accepted_by_user_id,
    data: data.data !== undefined ? data.data : demoOrganization.data,
  });
  return demoOrganization;
}

// ── Chat ──────────────────────────────────────────────────────────────────────
const ACCOUNTANT_ID = 'demo-accountant-0001';
const ACCOUNTANT_NAME = 'المحاسب أحمد';
const AI_ID = 'mohasby-ai';
const AI_NAME = 'Mohasby AI';
const CLIENT_NAME = 'نايف محمد';

// Helper for chat entries — keeps the conversation compact and consistent.
// `createdAt` is a pre-formatted local timestamp string.
const msg = (
  createdAt: string,
  who: 'ai' | 'accountant' | 'client',
  message: string,
  extra: Partial<ChatMessage> = {},
): ChatMessage => {
  const sender =
    who === 'ai'
      ? { user_id: AI_ID, user_name: AI_NAME }
      : who === 'accountant'
        ? { user_id: ACCOUNTANT_ID, user_name: ACCOUNTANT_NAME }
        : { user_id: DEMO_USER_ID, user_name: CLIENT_NAME };
  return {
    id: nextId('msg'),
    message,
    user_id: sender.user_id,
    user_name: sender.user_name,
    organization_id: DEMO_ORG_ID,
    created_at: createdAt,
    message_type: 'text',
    is_read_by_me: true,
    read_by_count: 2,
    ...extra,
  };
};

// Anchor the conversation to today's working hours so it always reads as a
// daytime chat (08:00 → ~16:00) regardless of when the demo is opened.
// offset = minutes after 08:00 local time today.
const dayAt = (offsetMin: number): string => {
  const d = now();
  d.setHours(8, 0, 0, 0);
  d.setMinutes(d.getMinutes() + offsetMin);
  return chatTime(d);
};

// A tiny valid (silent) WAV so voice messages are playable in the demo without
// erroring — the real backend would return a signed audio URL here.
export const DEMO_SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

// Thread replies keyed by their parent message id.
const threadReplies: Record<string, ChatMessage[]> = {};

const WELCOME_MESSAGE = `حياك الله ${CLIENT_NAME} في محاسبي

نرجو حجز اجتماعك الأول مع محاسبك للانطلاق. [MEETING_LINK]

كما نرجو التكرم باستكمال المهام المطلوبة حسب المتاح والمتوفر، للبدء في العمل المحاسبي، وفي حال وجود أي ملاحظات أو معلومات إضافية لم يتم ذكرها نرجوا مشاركتها. [TASKS_LINK]

شاكرين لكم

محاسبك: أحمد`;

// A natural conversation between a Saudi accountant and a retail client, mostly
// focused on the financial statements (income statement, balance sheet, cash
// flow, equity, trial balance). Spread across the working day (08:00 → ~15:30).
// No emojis, with voice notes, a quoted reply, a thread and a mention.
const buildConversation = (): ChatMessage[] => {
  const out: ChatMessage[] = [];
  let cursor = 0; // minutes after 08:00
  const add = (
    who: 'ai' | 'accountant' | 'client',
    message: string,
    gap: number,
    extra: Partial<ChatMessage> = {},
  ): ChatMessage => {
    cursor += gap;
    const m = msg(dayAt(cursor), who, message, extra);
    out.push(m);
    return m;
  };

  // ── Morning: intro + documents needed to build the statements ──
  add('ai', WELCOME_MESSAGE, 0, { response_type: 'welcome' });
  add('client', 'حياك الله أستاذ أحمد، وشكراً على الترحيب', 4);
  add('accountant', 'العفو أستاذ نايف، أنا محاسبك وبكون معك خطوة بخطوة', 3);
  add('client', 'تمام، وش أول خطوة؟', 3);
  add('accountant', 'أول شي نجهز قوائمك المالية الشهرية: قائمة الدخل، المركز المالي، والتدفقات النقدية', 3);
  add('client', 'ممتاز، هذا اللي أبيه بالضبط', 4);
  add('accountant', 'عشان نبني القوائم بدقة، نحتاج كشف الحساب البنكي وفواتير البيع والشراء والمصروفات', 3);
  add('client', 'وكيف أوصلها لكم؟', 3);
  const docsMsg = add('accountant', 'ترفعها مباشرة في قسم المستندات، ونحن نصنّفها ونرحّلها للقيود', 3);
  add('client', 'تمام، برفع كشف الحساب وفواتير الشهر الحين', 4);
  add('accountant', 'بالتوفيق، وأنا اراجعها أول ما توصل', 3);
  add('client', '', 4, { message_type: 'voice', audio_duration: 12, audio_file_path: 'demo/voice-client-1.webm' });

  // ── Late morning: income statement ──
  add('accountant', ' وصلتني المستندات. رفعت قائمة الدخل في خانة التقارير', 58);
  add('client', 'كم صافي الربح للشهر؟', 5);
  add('accountant', 'الإيرادات 480 ألف وتكلفة المبيعات 295 ألف، فمجمل الربح 185 ألف', 4);
  add('client', 'والمصروفات التشغيلية؟', 4);
  add('accountant', 'المصروفات التشغيلية 92 ألف، فصافي الربح قبل الزكاة 93 ألف', 4);
  add('client', 'ممتاز، هامش جيد', 4);
  add('accountant', 'نعم، هامش صافي الربح حوالي 19% وهو رقم ممتاز لنشاط التجزئة', 4);
  add('client', 'اقدر أشوف قائمة الدخل كاملة؟', 4);
  add('accountant', 'نعم، رفعتها في قسم التقارير، تلقاها جاهزة للاطلاع', 3);
  add('client', 'تمام، بطلع عليها', 4);

  // ── Midday: balance sheet (with a mention) ──
  add('accountant', '@نايف جهزت المركز المالي (الميزانية العمومية)، تلقاها في قسم التقارير', 49, { mentioned_user_ids: [DEMO_USER_ID] });
  add('client', '  وش إجمالي الأصول؟', 6);
  add('accountant', 'إجمالي الأصول 1.2 مليون، منها أصول متداولة 720 ألف وثابتة 480 ألف', 4);
  add('client', 'والالتزامات وحقوق الملكية؟', 4);
  add('accountant', 'الالتزامات 430 ألف وحقوق الملكية 770 ألف، والميزانية متوازنة', 4);
  add('client', 'ممتاز، الوضع المالي جيد', 5);
  add('accountant', 'نعم، نسبة السيولة جيدة والمديونية في حدود آمنة', 4);
  add('client', 'بخصوص المستندات اللي طلبتها، رفعتها باقي فواتير المشتريات', 5, {
    quoted_message_id: docsMsg.id,
    quoted_message: {
      id: docsMsg.id,
      message: docsMsg.message,
      user_name: docsMsg.user_name,
      message_type: 'text',
      created_at: docsMsg.created_at,
    },
  });
  add('accountant', 'وصلتني، شكراً. حدّثت القيود وانعكست على القوائم', 4);

  // ── Early afternoon: cash flow + equity ──
  add('accountant', 'جهزت قائمة التدفقات النقدية، وفيها تفاصيل التشغيل والاستثمار والتمويل', 64);
  add('client', 'كيف وضع التدفق التشغيلي؟', 5);
  add('accountant', 'التدفق التشغيلي موجب 110 ألف، وهذا مؤشر صحي للنشاط', 4);
  add('client', 'والتدفق من الاستثمار؟', 4);
  add('accountant', 'سالب 60 ألف بسبب شراء أصول ثابتة جديدة، وهذا طبيعي للتوسع', 5);
  add('accountant', '', 3, { message_type: 'voice', audio_duration: 17, audio_file_path: 'demo/voice-accountant-1.webm' });
  add('client', 'واضح تماماً، شكراً على الشرح', 5);
  add('accountant', 'العفو. وايضا جهزت قائمة التغيرات في حقوق الملكية ضمن نفس الحزمة', 4);

  // ── Afternoon: notes thread + trial balance + brief tax/zakat ──
  const threadParent = add('accountant', 'عندي ملاحظتين بسيطتين على القوائم، كتبتها بالثريد', 50, { thread_replies_count: 2 });
  threadReplies[threadParent.id] = [
    msg(dayAt(cursor + 2), 'accountant', 'الملاحظة الأولى: مصروف الإيجار يحتاج توزيع على أشهر السنة.', { thread_parent_id: threadParent.id }),
    msg(dayAt(cursor + 4), 'client', 'تمام، عدّلها باللي تشوفه مناسب.', { thread_parent_id: threadParent.id }),
  ];

  add('client', 'اطلعت عليها', 6);
  add('accountant', 'بالنسبة لميزان المراجعة، راجعته ومتوازن بدون فروقات', 5);
  add('client', 'ممتاز. ومتى نجهز الإقرار الربعي للقيمة المضافة؟', 6);
  add('accountant', 'الإقرار الربعي نجهزه قبل نهاية المهلة ونرفعه لك للاعتماد', 4);
  add('client', 'والزكاة؟', 4);
  add('accountant', 'الزكاة سنوية، نحتسبها بعد إقفال السنة ونجهز الإقرار الزكوي', 4);
  add('client', 'تمام، كل شي واضح', 5);

  // ── Late afternoon: approval + close ──
  add('accountant', 'تقدر تعتمد القوائم من قسم التقارير أو تطلب أي تعديل، وأنا جاهز', 46);
  add('client', 'اعتمدتها، شغل احترافي ومرتب', 6);
  add('accountant', 'يعطيك العافية أستاذ نايف، في خدمتك دايماً', 4);

  return out;
};

let chatMessages: ChatMessage[] = buildConversation();

export const getChatMessages = () => chatMessages;

const collectAllChatMessages = (): ChatMessage[] => {
  const all = [...chatMessages];
  for (const replies of Object.values(threadReplies)) {
    all.push(...replies);
  }
  return all;
};

export const searchDemoChatMessages = (query: string, limit = 50): ChatSearchResult[] => {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const matches = (msg: ChatMessage) => {
    const text = (msg.message || '').toLowerCase();
    const transcript = (msg.transcript || '').toLowerCase();
    return text.includes(q) || transcript.includes(q);
  };

  return collectAllChatMessages()
    .filter(matches)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit)
    .map((msg) => ({
      id: msg.id,
      message: msg.message,
      transcript: msg.transcript,
      user_id: msg.user_id,
      user_name: msg.user_name,
      created_at: msg.created_at,
      message_type: msg.message_type,
    }));
};

export const getDemoMessageContext = (messageId: string, before = 20, after = 20) => {
  let idx = chatMessages.findIndex((m) => m.id === messageId);

  if (idx === -1) {
    for (const [parentId, replies] of Object.entries(threadReplies)) {
      if (replies.some((r) => r.id === messageId)) {
        idx = chatMessages.findIndex((m) => m.id === parentId);
        break;
      }
    }
  }

  if (idx === -1) {
    const fallback = chatMessages.slice(-Math.min(before + after + 1, chatMessages.length));
    return {
      result_list: fallback,
      total_count: chatMessages.length,
      organization_members_count: demoOrgMembers.length,
    };
  }

  const start = Math.max(0, idx - before);
  const end = Math.min(chatMessages.length, idx + after + 1);
  return {
    result_list: chatMessages.slice(start, end),
    total_count: chatMessages.length,
    organization_members_count: demoOrgMembers.length,
  };
};

export const getDemoPinnedMessages = () => {
  const pinned = chatMessages.filter((msg) => msg.is_pinned);
  return {
    result_list: pinned,
    total_count: pinned.length,
    organization_members_count: 0,
  };
};

export const pinDemoChatMessage = (messageId: string, isPinned: boolean) => {
  const main = chatMessages.find((msg) => msg.id === messageId);
  if (!main) {
    const inThread = Object.values(threadReplies).some((replies) => replies.some((reply) => reply.id === messageId));
    const error = new Error(inThread ? 'Only main chat messages can be pinned' : 'Not found') as Error & {
      response?: { status: number; data: { message: string } };
    };
    error.response = {
      status: inThread ? 400 : 404,
      data: { message: inThread ? 'Only main chat messages can be pinned' : 'Not found' },
    };
    throw error;
  }
  main.is_pinned = isPinned;
  return { message_id: messageId, is_pinned: isPinned };
};

export const getThreadReplies = (parentId: string) => {
  const parent = chatMessages.find((m) => m.id === parentId) || chatMessages[0];
  const replies = threadReplies[parentId] || [];
  return { parent_message: parent, replies, total_replies: replies.length };
};

export const addDemoChatMessage = (message: string): ChatMessage => {
  const newMsg: ChatMessage = {
    id: nextId('msg'),
    message,
    user_id: DEMO_USER_ID,
    user_name: CLIENT_NAME,
    organization_id: DEMO_ORG_ID,
    created_at: chatTime(now()),
    message_type: 'text',
    is_read_by_me: true,
    read_by_count: 1,
  };
  chatMessages = [...chatMessages, newMsg];
  return newMsg;
};

export const demoOrgMembers = [
  { id: ACCOUNTANT_ID, name: ACCOUNTANT_NAME },
  { id: DEMO_USER_ID, name: CLIENT_NAME },
  { id: 'demo-user-0002', name: 'سارة الموظفة' },
];

// Canonical demo user (the client) — used to seed/refresh the fake session so
// the chat always shows "نايف محمد" rather than a stale name or the org name.
export const buildDemoUser = (): User => ({
  id: DEMO_USER_ID,
  email: 'naif@alufuq.com',
  name: CLIENT_NAME,
  phone: '966544825445',
  data: {
    role: Role.ORGANIZATION,
    phone: '966544825445',
    access: [
      {
        organization_id: DEMO_ORG_ID,
        team_id: null,
        permissions: [],
        custom_permissions: ['access_invoices', 'access_reports', 'access_chat'],
      },
    ],
  },
  organization: demoOrganization,
});

// ── Tasks ───────────────────────────────────────────────────────────────────
let tasks: ReportingTask[] = [
  {
    id: nextId('task'),
    organization_id: DEMO_ORG_ID,
    accountant_id: ACCOUNTANT_ID,
    title: 'توقيع عقد الاشتراك',
    description: 'راجع عقد اشتراكك ووقّعه لإتمام تفعيل الخدمة.',
    task_type: 'onboarding',
    template_key: 'sign_subscription_contract',
    cutoff_date: iso(daysFromNow(7)),
    submission_date: null,
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'not_complete',
    is_active: true,
    is_required: true,
    attachment: null,
    created_at: iso(daysAgo(1)),
    updated_at: iso(daysAgo(1)),
  },
  {
    id: nextId('task'),
    organization_id: DEMO_ORG_ID,
    accountant_id: ACCOUNTANT_ID,
    title: 'جدولة الاجتماع الأول',
    description: 'احجز موعدك الأول مع المحاسب للتعرف على نشاط مؤسستك.',
    task_type: 'onboarding',
    template_key: 'schedule_first_meeting',
    cutoff_date: iso(daysFromNow(5)),
    submission_date: null,
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'not_complete',
    is_active: true,
    is_required: true,
    attachment: null,
    created_at: iso(daysAgo(2)),
    updated_at: iso(daysAgo(2)),
  },
  {
    id: nextId('task'),
    organization_id: DEMO_ORG_ID,
    accountant_id: ACCOUNTANT_ID,
    title: 'السجل التجاري',
    description: 'ارفع نسخة من السجل التجاري للمؤسسة.',
    task_type: 'onboarding',
    template_key: 'commercial_record',
    cutoff_date: iso(daysFromNow(2)),
    submission_date: null,
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'not_complete',
    is_active: true,
    is_required: true,
    attachment: null,
    created_at: iso(daysAgo(2)),
    updated_at: iso(daysAgo(2)),
  },
  {
    id: nextId('task'),
    organization_id: DEMO_ORG_ID,
    accountant_id: ACCOUNTANT_ID,
    title: 'شهادة ضريبة القيمة المضافة',
    description: 'ارفع شهادة التسجيل في ضريبة القيمة المضافة.',
    task_type: 'onboarding',
    template_key: 'vat_certificate',
    cutoff_date: iso(daysFromNow(-1)),
    submission_date: null,
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'overdue',
    is_active: true,
    is_required: false,
    attachment: null,
    created_at: iso(daysAgo(4)),
    updated_at: iso(daysAgo(4)),
  },
  {
    id: nextId('task'),
    organization_id: DEMO_ORG_ID,
    accountant_id: ACCOUNTANT_ID,
    title: 'العنوان الوطني',
    description: 'ارفع وثيقة العنوان الوطني للمؤسسة.',
    task_type: 'onboarding',
    template_key: 'national_address',
    cutoff_date: iso(daysAgo(6)),
    submission_date: iso(daysAgo(5)),
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'complete',
    is_active: true,
    is_required: true,
    attachment: null,
    created_at: iso(daysAgo(8)),
    updated_at: iso(daysAgo(5)),
  },
];

export const getTasks = () => tasks;

export const DEMO_CONTRACT_ID = 'demo-contract-pending';

export const getDemoPendingContract = () => {
  const signTask = tasks.find(
    (t) => t.template_key === 'sign_subscription_contract' && t.status !== 'complete',
  );
  if (!signTask) return null;
  return {
    id: DEMO_CONTRACT_ID,
    organization_id: DEMO_ORG_ID,
    status: 'pending_signature' as const,
    organization_name: demoOrganization.name,
    plan_code: demoSubscription.plan.code,
    effective_date: demoSubscription.started_at,
    period_start: demoSubscription.started_at,
    period_end: demoSubscription.expires_at,
    total_fee: demoSubscription.plan.prices.annual ?? '12000',
    installments_total: null,
    services: [{ ar: 'محاسب عن بُعد', en: 'Remote accountant' }],
    sign_url_path: `/contracts/${DEMO_CONTRACT_ID}/sign`,
  };
};

export const completeDemoSignContractTask = () => {
  tasks = tasks.map((t) =>
    t.template_key === 'sign_subscription_contract'
      ? { ...t, status: 'complete', submission_date: iso(now()), updated_at: iso(now()) }
      : t,
  );
};

export const submitDemoTask = (taskId: string): ReportingTask | null => {
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return null;
  const updated: ReportingTask = {
    ...tasks[idx],
    status: 'complete',
    submission_date: iso(now()),
    updated_at: iso(now()),
  };
  tasks = tasks.map((t) => (t.id === taskId ? updated : t));
  return updated;
};

export const createDemoTask = (data: {
  organization_id: string;
  title: string;
  description: string;
  cutoff_date: string;
  asset_id?: string | null;
}): ReportingTask => {
  const task: ReportingTask = {
    id: nextId('task'),
    organization_id: data.organization_id,
    accountant_id: ACCOUNTANT_ID,
    title: data.title,
    description: data.description,
    task_type: 'reporting',
    template_key: null,
    cutoff_date: data.cutoff_date,
    submission_date: null,
    sla_deadline: null,
    expected_sla_deadline: null,
    status: 'not_complete',
    is_active: true,
    attachment: data.asset_id
      ? { id: data.asset_id, filename: 'demo-attachment.pdf', file_path: '#demo' }
      : null,
    created_at: iso(now()),
    updated_at: iso(now()),
  };
  tasks = [task, ...tasks];
  return task;
};

export const updateDemoTask = (
  taskId: string,
  data: Partial<Pick<ReportingTask, 'title' | 'description' | 'cutoff_date' | 'status'>> & {
    asset_id?: string | null;
  },
): ReportingTask | null => {
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return null;
  const current = tasks[idx];
  const cutoff_date = data.cutoff_date ?? current.cutoff_date;
  const status =
    data.status ??
    (current.status === 'overdue' && new Date(cutoff_date).getTime() > Date.now()
      ? 'not_complete'
      : current.status);

  let attachment = current.attachment;
  if (data.asset_id === null) {
    attachment = null;
  } else if (data.asset_id) {
    attachment = { id: data.asset_id, filename: 'demo-attachment.pdf', file_path: '#demo' };
  }

  const updated: ReportingTask = {
    ...current,
    ...data,
    cutoff_date,
    status,
    attachment,
    updated_at: iso(now()),
  };
  tasks = tasks.map((t) => (t.id === taskId ? updated : t));
  return updated;
};

// ── Documents (invoices + folders) ───────────────────────────────────────────
let invoices: Invoice[] = [
  {
    id: 'folder-purchases',
    organization_id: DEMO_ORG_ID,
    filename: 'فواتير المشتريات',
    created_at: iso(daysAgo(20)),
    parent_id: undefined,
    type: 'folder',
    access_level: 'public',
    data: { name: 'فواتير المشتريات', folder_key: 'purchase_invoices' },
    entered: false,
    entered_at: null,
    entered_by: null,
  },
  {
    id: 'folder-sales',
    organization_id: DEMO_ORG_ID,
    filename: 'فواتير المبيعات',
    created_at: iso(daysAgo(20)),
    parent_id: undefined,
    type: 'folder',
    access_level: 'public',
    data: { name: 'فواتير المبيعات', folder_key: 'sales_invoices' },
  },
  {
    id: 'folder-bank',
    organization_id: DEMO_ORG_ID,
    filename: 'كشوفات البنك',
    created_at: iso(daysAgo(20)),
    parent_id: undefined,
    type: 'folder',
    access_level: 'public',
    data: { name: 'كشوفات البنك', folder_key: 'bank_statement' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة كهرباء - مارس.pdf',
    created_at: iso(daysAgo(8)),
    parent_id: undefined,
    type: 'file',
    file_path: 'demo/electricity-march.pdf',
    access_level: 'public',
    ocr_status: 'completed',
    data: { name: 'فاتورة كهرباء - مارس.pdf' },
    entered: true,
    entered_at: iso(daysAgo(1)),
    entered_by: DEMO_USER_ID,
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة إيجار المكتب.pdf',
    created_at: iso(daysAgo(6)),
    parent_id: undefined,
    type: 'file',
    file_path: 'demo/office-rent.pdf',
    access_level: 'public',
    ocr_status: 'completed',
    data: { name: 'فاتورة إيجار المكتب.pdf' },
    entered: true,
    entered_at: iso(daysAgo(2)),
    entered_by: DEMO_USER_ID,
  },
  // children of purchases folder
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'مشتريات مورد الرياض.pdf',
    created_at: iso(daysAgo(7)),
    parent_id: 'folder-purchases',
    type: 'file',
    file_path: 'demo/supplier-riyadh.pdf',
    access_level: 'public',
    data: { name: 'مشتريات مورد الرياض.pdf' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'مشتريات مورد جدة.pdf',
    created_at: iso(daysAgo(5)),
    parent_id: 'folder-purchases',
    type: 'file',
    file_path: 'demo/supplier-jeddah.pdf',
    access_level: 'public',
    data: { name: 'مشتريات مورد جدة.pdf' },
  },
  {
    id: 'folder-expenses',
    organization_id: DEMO_ORG_ID,
    filename: 'المصروفات التشغيلية',
    created_at: iso(daysAgo(18)),
    parent_id: undefined,
    type: 'folder',
    access_level: 'public',
    data: { name: 'المصروفات التشغيلية', folder_key: 'recurring_expenses' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة إنترنت ومكالمات.pdf',
    created_at: iso(daysAgo(3)),
    parent_id: undefined,
    type: 'file',
    file_path: 'demo/internet.pdf',
    access_level: 'public',
    ocr_status: 'completed',
    data: { name: 'فاتورة إنترنت ومكالمات.pdf' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'إيصال صيانة سيارة الشركة.jpg',
    created_at: iso(daysAgo(2)),
    parent_id: undefined,
    type: 'file',
    file_path: 'demo/car-maintenance.jpg',
    access_level: 'private',
    ocr_status: 'processing',
    data: { name: 'إيصال صيانة سيارة الشركة.jpg' },
  },
  // children of purchases folder
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'مشتريات مورد الدمام.pdf',
    created_at: iso(daysAgo(3)),
    parent_id: 'folder-purchases',
    type: 'file',
    file_path: 'demo/supplier-dammam.pdf',
    access_level: 'public',
    data: { name: 'مشتريات مورد الدمام.pdf' },
  },
  // children of sales folder
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة مبيعات #1024.pdf',
    created_at: iso(daysAgo(4)),
    parent_id: 'folder-sales',
    type: 'file',
    file_path: 'demo/sales-1024.pdf',
    access_level: 'public',
    data: { name: 'فاتورة مبيعات #1024.pdf' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة مبيعات #1025.pdf',
    created_at: iso(daysAgo(3)),
    parent_id: 'folder-sales',
    type: 'file',
    file_path: 'demo/sales-1025.pdf',
    access_level: 'public',
    data: { name: 'فاتورة مبيعات #1025.pdf' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'فاتورة مبيعات #1026.pdf',
    created_at: iso(daysAgo(1)),
    parent_id: 'folder-sales',
    type: 'file',
    file_path: 'demo/sales-1026.pdf',
    access_level: 'public',
    data: { name: 'فاتورة مبيعات #1026.pdf' },
  },
  // children of expenses folder
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'رواتب الموظفين - مارس.xlsx',
    created_at: iso(daysAgo(5)),
    parent_id: 'folder-expenses',
    type: 'file',
    file_path: 'demo/payroll-march.xlsx',
    access_level: 'private',
    data: { name: 'رواتب الموظفين - مارس.xlsx' },
  },
  {
    id: nextId('inv'),
    organization_id: DEMO_ORG_ID,
    filename: 'اشتراك برنامج المحاسبة.pdf',
    created_at: iso(daysAgo(4)),
    parent_id: 'folder-expenses',
    type: 'file',
    file_path: 'demo/software-subscription.pdf',
    access_level: 'public',
    data: { name: 'اشتراك برنامج المحاسبة.pdf' },
  },
];

invoices = invoices.map((item, index): Invoice => ({
  ...item,
  uploaded_by_name: index % 6 === 0 ? null : item.type === 'folder' ? ACCOUNTANT_NAME : CLIENT_NAME,
}));

export const getInvoices = () => invoices;

export const addDemoInvoice = (data: {
  filename?: string;
  parent_id?: string;
  type?: 'file' | 'folder';
  data?: { name?: string; notes?: string };
}): Invoice => {
  const inv: Invoice = {
    id: nextId(data.type === 'folder' ? 'folder' : 'inv'),
    organization_id: DEMO_ORG_ID,
    filename: data.filename || data.data?.name || 'مستند جديد.pdf',
    created_at: iso(now()),
    parent_id: data.parent_id || undefined,
    type: data.type || 'file',
    file_path: data.type === 'folder' ? undefined : `demo/${nextId('file')}.pdf`,
    access_level: 'public',
    data: data.data,
    entered: false,
    entered_at: null,
    entered_by: null,
    uploaded_by_name: CLIENT_NAME,
  };
  invoices = [...invoices, inv];
  return inv;
};

export const deleteDemoInvoice = (id: string) => {
  invoices = invoices.filter((i) => i.id !== id && i.parent_id !== id);
};

export const setDemoInvoiceEntered = (id: string, entered: boolean) => {
  const invoice = invoices.find((item) => item.id === id);
  if (!invoice) {
    const error = new Error('Document not found') as Error & {
      response: { status: number; data: { message: string } };
    };
    error.response = { status: 404, data: { message: 'Document not found' } };
    throw error;
  }

  const updated: Invoice = {
    ...invoice,
    entered,
    entered_at: entered ? iso(now()) : null,
    entered_by: entered ? DEMO_USER_ID : null,
  };
  invoices = invoices.map((item) => (item.id === id ? updated : item));
  return {
    id: updated.id,
    entered: Boolean(updated.entered),
    entered_at: updated.entered_at ?? null,
    entered_by: updated.entered_by ?? null,
  };
};

// ── Reports ─────────────────────────────────────────────────────────────────
const reports: Report[] = [
  {
    id: 'report-income',
    organization_id: DEMO_ORG_ID,
    name: 'قائمة الدخل - مارس 2026',
    type: ReportType.INCOME_STATEMENT,
    processing_status: ProcessingStatus.APPROVED,
    source_type: SourceType.EXCEL,
    from_date: '2026-03-01',
    to_date: '2026-03-31',
    filename: 'income-statement-march.xlsx',
    created_at: iso(daysAgo(3)),
  },
  {
    id: 'report-balance',
    organization_id: DEMO_ORG_ID,
    name: 'الميزانية العمومية - مارس 2026',
    type: ReportType.BALANCE_SHEET,
    processing_status: ProcessingStatus.APPROVED,
    source_type: SourceType.EXCEL,
    from_date: '2026-03-01',
    to_date: '2026-03-31',
    filename: 'balance-sheet-march.xlsx',
    created_at: iso(daysAgo(3)),
  },
  {
    id: 'report-cashflow',
    organization_id: DEMO_ORG_ID,
    name: 'قائمة التدفقات النقدية - مارس 2026',
    type: ReportType.CASH_FLOW,
    processing_status: ProcessingStatus.READY,
    source_type: SourceType.PDF,
    from_date: '2026-03-01',
    to_date: '2026-03-31',
    filename: 'cash-flow-march.pdf',
    created_at: iso(daysAgo(2)),
  },
];

export const getReports = () => reports;

const buildReportData = (type: ReportType, name: string, from: string, to: string) => {
  if (type === ReportType.BALANCE_SHEET) {
    return {
      name,
      currency: 'SAR',
      from_date: from,
      to_date: to,
      current_assets: [
        { name_en: 'Cash', name_ar: 'النقد', amount: 210000, pct: 35 },
        { name_en: 'Accounts Receivable', name_ar: 'الذمم المدينة', amount: 145000, pct: 24 },
        { name_en: 'Inventory', name_ar: 'المخزون', amount: 95000, pct: 16 },
      ],
      total_current_assets: 450000,
      fixed_assets: [
        { name_en: 'Equipment', name_ar: 'المعدات', cost: 180000, accumulated_depreciation: 30000, net: 150000, pct: 25 },
      ],
      net_fixed_assets: 150000,
      total_assets: 600000,
      current_liabilities: [
        { name_en: 'Accounts Payable', name_ar: 'الذمم الدائنة', amount: 120000, pct: 20 },
        { name_en: 'VAT Payable', name_ar: 'ضريبة القيمة المضافة المستحقة', amount: 30000, pct: 5 },
      ],
      total_liabilities: 150000,
      capital: 350000,
      equity_items: [
        { name_en: 'Capital', name_ar: 'رأس المال', amount: 350000 },
        { name_en: 'Retained Earnings', name_ar: 'الأرباح المبقاة', amount: 100000 },
      ],
      total_owner_equity: 450000,
      total_liabilities_equity: 600000,
      balance: 0,
    };
  }
  if (type === ReportType.CASH_FLOW) {
    return {
      name,
      currency: 'SAR',
      from_date: from,
      to_date: to,
      net_income: 187000,
      adjustments: [
        { name_en: 'Depreciation', name_ar: 'الإهلاك', amount: 30000 },
        { name_en: 'Change in Receivables', name_ar: 'التغير في الذمم المدينة', amount: -25000 },
      ],
      change_in_cash: 192000,
      beginning_cash: 18000,
      ending_cash: 210000,
    };
  }
  // Default: income statement
  return {
    name,
    currency: 'SAR',
    from_date: from,
    to_date: to,
    revenue_items: [
      { name_en: 'Sales Revenue', name_ar: 'إيرادات المبيعات', amount: 320000 },
      { name_en: 'Service Revenue', name_ar: 'إيرادات الخدمات', amount: 85000 },
    ],
    gross_profit: 405000,
    expenses: [
      { name_en: 'Salaries', name_ar: 'الرواتب', amount: 120000 },
      { name_en: 'Rent', name_ar: 'الإيجار', amount: 45000 },
      { name_en: 'Utilities', name_ar: 'المرافق', amount: 18000 },
      { name_en: 'Marketing', name_ar: 'التسويق', amount: 32000 },
    ],
    total_expenses: 215000,
    net_operation_income: 190000,
    other_revenues: 5000,
    other_expenses: 8000,
    net_income: 187000,
  };
};

export const getReportDetails = (reportId: string) => {
  const base = reports.find((r) => r.id === reportId) || reports[0];
  return {
    id: base.id,
    organization_id: DEMO_ORG_ID,
    asset_id: 'demo-asset',
    pdf_asset_id: null,
    filename: base.filename,
    pdf_filename: null,
    file_path: 'demo/report.xlsx',
    type: base.type,
    processing_status: base.processing_status,
    source_type: base.source_type,
    processing_error: null,
    data: buildReportData(base.type, base.name, base.from_date || '2026-03-01', base.to_date || '2026-03-31'),
    created_at: base.created_at,
  };
};

// ── Settings — organization users ─────────────────────────────────────────────
export const demoOrganizationUsers: OrganizationUser[] = [
  {
    id: DEMO_USER_ID,
    email: 'naif@alufuq.com',
    name: 'نايف محمد',
    phone: '+966500000000',
    role: 'organization',
    status: 'ACTIVE',
    custom_permissions: ['access_invoices', 'access_reports', 'access_chat'],
    created_at: iso(daysAgo(60)),
  },
  {
    id: 'demo-user-0002',
    email: 'sara@alufuq.com',
    name: 'سارة الموظفة',
    phone: '+966500000001',
    role: 'organization_user',
    status: 'ACTIVE',
    custom_permissions: ['access_invoices', 'access_reports'],
    created_at: iso(daysAgo(40)),
  },
  {
    id: 'demo-user-0003',
    email: 'khalid@alufuq.com',
    name: 'خالد المندوب',
    phone: '+966500000002',
    role: 'organization_user',
    status: 'PENDING',
    custom_permissions: ['access_invoices'],
    created_at: iso(daysAgo(10)),
  },
];

export const demoSubscription = {
  id: 'demo-sub-0001',
  organization_id: DEMO_ORG_ID,
  status: 'active' as const,
  plan_id: 'demo-plan-pro',
  billing_cycle: 'annual' as const,
  started_at: iso(daysAgo(60)),
  expires_at: iso(daysFromNow(305)),
  cancelled_at: null,
  installments_total: null,
  installments_paid_count: 0,
  next_installment_due_at: null,
  show_installment_due_banner: false,
  days_until_next_installment_due: null,
  pending_installment_payment_url: null,
  pending_installment_payment_id: null,
  pending_installment_number: null,
  show_renewal_due_banner: false,
  days_until_subscription_expires: null,
  pending_renewal_payment_url: null,
  pending_renewal_payment_id: null,
  pending_contract: null,
  plan: {
    id: 'demo-plan-pro',
    code: 'remote_accountant_sme',
    name_ar: 'محاسب عن بُعد - للمنشآت',
    name_en: 'Remote Accountant - SME',
    prices: { annual: '12000' },
    annual_installment_options: [],
    features: { sections: [{ items: ['محاسب مختص', 'تقارير شهرية', 'دعم مباشر'] }] },
    sort_order: 1,
    is_active: true,
  },
};

type DemoFollowUpTag = {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
};

let demoPaymentFollowUpTags: DemoFollowUpTag[] = [
  { id: '01JGPAYTAG0000000000000001', name: 'العميل غير متجاوب', sort_order: 0, is_active: true, is_system: true },
  { id: '01JGPAYTAG0000000000000002', name: 'تم ايقاف الاشتراك', sort_order: 1, is_active: true, is_system: true },
  { id: '01JGPAYTAG0000000000000003', name: 'تم ايقاف النشاط', sort_order: 2, is_active: true, is_system: true },
  { id: '01JGPAYTAG0000000000000004', name: 'طلب تأجيل الدفع', sort_order: 3, is_active: true, is_system: true },
  { id: '01JGPAYTAG0000000000000005', name: 'عميل شريك', sort_order: 4, is_active: true, is_system: true },
  { id: 'demo-tag-will-pay', name: 'قال بيسدد', sort_order: 10, is_active: true, is_system: false },
  { id: 'demo-tag-follow-up', name: 'يحتاج متابعة ثانية', sort_order: 11, is_active: true, is_system: false },
];

let demoOrganizationFollowUpTags: Record<string, string[]> = {
  [DEMO_ORG_ID]: ['01JGPAYTAG0000000000000001'],
  'demo-org-overdue': ['01JGPAYTAG0000000000000004', 'demo-tag-will-pay'],
  'demo-org-clear': [],
};

export const getDemoPaymentFollowUpTags = () =>
  [...demoPaymentFollowUpTags].sort((a, b) => a.sort_order - b.sort_order);

export const createDemoPaymentFollowUpTag = (name: string, sortOrder?: number) => {
  const normalized = name.trim();
  if (demoPaymentFollowUpTags.some((tag) => tag.name === normalized)) {
    throw { response: { status: 409, data: { message: 'Duplicate tag name' } } };
  }
  const tag: DemoFollowUpTag = {
    id: `demo-tag-${Date.now()}`,
    name: normalized,
    sort_order: sortOrder ?? demoPaymentFollowUpTags.length + 1,
    is_active: true,
    is_system: false,
  };
  demoPaymentFollowUpTags = [...demoPaymentFollowUpTags, tag];
  return tag;
};

export const updateDemoPaymentFollowUpTag = (
  tagId: string,
  patch: Partial<Pick<DemoFollowUpTag, 'name' | 'sort_order' | 'is_active'>>,
) => {
  const existing = demoPaymentFollowUpTags.find((tag) => tag.id === tagId);
  if (!existing) throw { response: { status: 404 } };
  if (existing.is_system && patch.name != null && patch.name !== existing.name) {
    throw { response: { status: 403, data: { message: 'System tags cannot be deleted or renamed' } } };
  }
  demoPaymentFollowUpTags = demoPaymentFollowUpTags.map((tag) =>
    tag.id === tagId ? { ...tag, ...patch } : tag,
  );
  return demoPaymentFollowUpTags.find((tag) => tag.id === tagId)!;
};

export const deleteDemoPaymentFollowUpTag = (tagId: string) => {
  const existing = demoPaymentFollowUpTags.find((tag) => tag.id === tagId);
  if (existing?.is_system) {
    throw { response: { status: 403, data: { message: 'System tags cannot be deleted or renamed' } } };
  }
  demoPaymentFollowUpTags = demoPaymentFollowUpTags.filter((tag) => tag.id !== tagId);
  demoOrganizationFollowUpTags = Object.fromEntries(
    Object.entries(demoOrganizationFollowUpTags).map(([orgId, tagIds]) => [
      orgId,
      tagIds.filter((id) => id !== tagId),
    ]),
  );
};

export const setDemoOrganizationFollowUpTags = (organizationId: string, tagIds: string[]) => {
  demoOrganizationFollowUpTags = { ...demoOrganizationFollowUpTags, [organizationId]: tagIds };
  return tagIds
    .map((id) => demoPaymentFollowUpTags.find((tag) => tag.id === id))
    .filter(Boolean)
    .map((tag) => ({ id: tag!.id, name: tag!.name }));
};

export const getDemoPaymentMonitoring = (params?: {
  search?: string;
  due_in_month?: string;
  overdue_only?: boolean;
  tag_id?: string;
  amount_min?: string;
  amount_max?: string;
  amount_field?: 'amount_due_now' | 'remaining_total_with_vat';
  payment_due_type?: 'installment' | 'renewal' | 'none';
  plan_code?: string;
  billing_cycle?: string;
  subscription_status?: string;
  service_status?: string;
  limit?: number;
  offset?: number;
}) => {
  const resolveOrgTags = (organizationId: string) => {
    const tagIds = demoOrganizationFollowUpTags[organizationId] ?? [];
    return tagIds
      .map((id) => demoPaymentFollowUpTags.find((tag) => tag.id === id))
      .filter(Boolean)
      .map((tag) => ({ id: tag!.id, name: tag!.name }));
  };

  const allItems = [
    {
      organization_id: DEMO_ORG_ID,
      organization_name: demoOrganization.name,
      organization_email: demoOrganization.email,
      follow_up_tags: resolveOrgTags(DEMO_ORG_ID),
      plan_code: demoSubscription.plan.code,
      plan_name_ar: demoSubscription.plan.name_ar,
      plan_name_en: demoSubscription.plan.name_en,
      billing_cycle: 'annual' as const,
      subscription_status: 'active' as const,
      payment_due_type: 'installment' as const,
      due_date: iso(daysFromNow(10)),
      days_until_due: 10,
      is_overdue: false,
      amount_due_now: '2875.00',
      remaining_total: '8625.00',
      remaining_total_with_vat: '9918.75',
      installments_paid_count: 1,
      installments_total: 4,
      installments_remaining: 3,
      subscription_expires_at: demoSubscription.expires_at,
      days_until_expiry: 305,
      pending_payment_id: 'demo-payment-1',
      pending_payment_url: 'https://moyasar.com/demo-payment',
    },
    {
      organization_id: 'demo-org-pending',
      organization_name: 'شركة معلّقة',
      organization_email: 'pending@example.com',
      follow_up_tags: resolveOrgTags('demo-org-pending'),
      service_status: 'pending' as const,
      plan_code: demoSubscription.plan.code,
      plan_name_ar: demoSubscription.plan.name_ar,
      plan_name_en: demoSubscription.plan.name_en,
      billing_cycle: 'annual' as const,
      subscription_status: 'active' as const,
      payment_due_type: 'installment' as const,
      due_date: iso(daysFromNow(14)),
      days_until_due: 14,
      is_overdue: false,
      amount_due_now: '2875.00',
      remaining_total: '5750.00',
      remaining_total_with_vat: '6612.50',
      installments_paid_count: 2,
      installments_total: 4,
      installments_remaining: 2,
      subscription_expires_at: demoSubscription.expires_at,
      days_until_expiry: 280,
      pending_payment_id: 'demo-payment-pending',
      pending_payment_url: 'https://moyasar.com/demo-payment-pending',
    },
    {
      organization_id: 'demo-org-overdue',
      organization_name: 'شركة متأخرة',
      organization_email: 'late@example.com',
      follow_up_tags: resolveOrgTags('demo-org-overdue'),
      plan_code: demoSubscription.plan.code,
      plan_name_ar: demoSubscription.plan.name_ar,
      plan_name_en: demoSubscription.plan.name_en,
      billing_cycle: 'annual' as const,
      subscription_status: 'active' as const,
      payment_due_type: 'renewal' as const,
      due_date: iso(daysAgo(5)),
      days_until_due: -5,
      is_overdue: true,
      amount_due_now: '13800.00',
      remaining_total: '12000.00',
      remaining_total_with_vat: '13800.00',
      installments_paid_count: 4,
      installments_total: 4,
      installments_remaining: 0,
      subscription_expires_at: iso(daysAgo(5)),
      days_until_expiry: -5,
      pending_payment_id: null,
      pending_payment_url: null,
    },
    {
      organization_id: 'demo-org-clear',
      organization_name: 'شركة بدون مستحق',
      organization_email: 'clear@example.com',
      follow_up_tags: resolveOrgTags('demo-org-clear'),
      plan_code: demoSubscription.plan.code,
      plan_name_ar: demoSubscription.plan.name_ar,
      plan_name_en: demoSubscription.plan.name_en,
      billing_cycle: 'one_time' as const,
      subscription_status: 'active' as const,
      payment_due_type: 'none' as const,
      due_date: null,
      days_until_due: null,
      is_overdue: false,
      amount_due_now: null,
      remaining_total: null,
      remaining_total_with_vat: null,
      installments_paid_count: 0,
      installments_total: null,
      installments_remaining: null,
      subscription_expires_at: null,
      days_until_expiry: null,
      pending_payment_id: null,
      pending_payment_url: null,
    },
  ];

  const filtered = filterPaymentMonitoringItems(allItems, {
    search: params?.search ?? '',
    dueInMonth: params?.due_in_month ?? '',
    overdueOnly: params?.overdue_only ?? false,
    tagId: params?.tag_id ?? '',
    amountMin: params?.amount_min ?? '',
    amountMax: params?.amount_max ?? '',
    amountField: params?.amount_field ?? 'amount_due_now',
    dueType: params?.payment_due_type ?? '',
    planCode: params?.plan_code ?? '',
    billingCycle: params?.billing_cycle ?? '',
    subscriptionStatus: params?.subscription_status ?? '',
    serviceStatus: params?.service_status ?? '',
  });

  const offset = params?.offset ?? 0;
  const limit = params?.limit ?? 100;
  const items = filtered.slice(offset, offset + limit);

  return {
    summary: computePaymentMonitoringSummary(filtered),
    items,
    total: filtered.length,
    limit,
    offset,
  };
};

const demoYearMonth = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const shiftDemoMonth = (month: string, delta: number) => {
  const [year, mon] = month.split('-').map(Number);
  return demoYearMonth(new Date(year, mon - 1 + delta, 1));
};

type DemoProfitAssignment = {
  id: string;
  accountant_id: string;
  kind: 'assistant' | 'accountant' | 'organization';
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  started_at: string;
  ended_at: string | null;
};

const DEMO_PC_PEOPLE: Record<string, { name: string; email: string; role: Role }> = {
  'demo-pc-1': { name: 'أحمد المحاسب', email: 'ahmed@demo.com', role: Role.ACCOUNTANT },
  'demo-pc-2': { name: 'محمد', email: 'mohammed@demo.com', role: Role.ACCOUNTANT },
  'demo-clearing-1': { name: 'سارة', email: 'sara@demo.com', role: Role.CLEARING_OFFICER },
  'demo-accountant-2': { name: 'خالد', email: 'khaled@demo.com', role: Role.ACCOUNTANT },
};

export const getDemoStaffUsers = (): User[] =>
  Object.entries(DEMO_PC_PEOPLE).map(([id, person]) => ({
    id,
    name: person.name,
    email: person.email,
    data: { role: person.role, access: [] },
  }));

let demoProfitAssignments: DemoProfitAssignment[] | null = null;

const utcInMonth = (month: string, day: number, hourUtc: number) =>
  `${month}-${String(day).padStart(2, '0')}T${String(hourUtc).padStart(2, '0')}:28:00`;

const seedDemoProfitAssignments = (): DemoProfitAssignment[] => {
  const current = demoYearMonth();
  const previous = shiftDemoMonth(current, -1);
  return [
    {
      id: 'demo-asg-assistant',
      accountant_id: 'demo-pc-1',
      kind: 'assistant',
      user_id: 'demo-clearing-1',
      user_name: 'سارة',
      user_email: 'sara@demo.com',
      organization_id: null,
      organization_name: null,
      started_at: utcInMonth(current, 4, 7),
      ended_at: null,
    },
    {
      id: 'demo-asg-accountant',
      accountant_id: 'demo-pc-1',
      kind: 'accountant',
      user_id: 'demo-accountant-2',
      user_name: 'خالد',
      user_email: 'khaled@demo.com',
      organization_id: null,
      organization_name: null,
      started_at: utcInMonth(current, 8, 9),
      ended_at: utcInMonth(current, 16, 15),
    },
    {
      id: 'demo-asg-org',
      accountant_id: 'demo-pc-1',
      kind: 'organization',
      user_id: null,
      user_name: null,
      user_email: null,
      organization_id: DEMO_ORG_ID,
      organization_name: 'مؤسسة الأفق التجارية',
      started_at: utcInMonth(previous, 11, 6),
      ended_at: utcInMonth(current, 19, 12),
    },
  ];
};

const profitAssignments = () => {
  if (!demoProfitAssignments) demoProfitAssignments = seedDemoProfitAssignments();
  return demoProfitAssignments;
};

const eventsInMonth = (row: DemoProfitAssignment, month: string) => {
  const base = {
    assignment_id: row.id,
    user_id: row.user_id,
    user_name: row.user_name,
    user_email: row.user_email,
    organization_id: row.organization_id,
    organization_name: row.organization_name,
  };
  const events = [];
  if (row.started_at.startsWith(month)) {
    events.push({ ...base, type: `${row.kind}_joined`, at: row.started_at });
  }
  if (row.ended_at?.startsWith(month)) {
    events.push({ ...base, type: `${row.kind}_left`, at: row.ended_at });
  }
  return events.sort((a, b) => (a.at < b.at ? 1 : -1));
};

const demoProfitCenters = () => [
  {
    id: 'demo-pc-1',
    name: 'Egypt-1',
    location: 'EGY' as const,
    lead_accountant: {
      id: 'demo-pc-1',
      name: 'أحمد المحاسب',
      email: 'ahmed@demo.com',
      annual_salary: '120000.00',
    },
    assistants: [
      {
        id: 'demo-clearing-1',
        name: 'سارة',
        email: 'sara@demo.com',
        annual_salary: '72000.00',
      },
    ],
    metrics: {
      cost: '16000.00',
      subscription_revenue_monthly: '8500.00',
      avg_invoices_upper_bound: 45,
      tax_registration_count: 6,
      sectors: { مقاولات: 2, تجزئة: 1 },
      org_types: { company: 2, establishment: 1 },
      overdue_collection_total: '13800.00',
      organizations_count: 8,
    },
  },
  {
    id: 'demo-pc-2',
    name: 'Syria-1',
    location: 'SYR' as const,
    lead_accountant: {
      id: 'demo-pc-2',
      name: 'محمد',
      email: 'mohammed@demo.com',
      annual_salary: null,
    },
    assistants: [],
    metrics: {
      cost: null,
      subscription_revenue_monthly: '0.00',
      avg_invoices_upper_bound: null,
      tax_registration_count: 0,
      sectors: {},
      org_types: {},
      overdue_collection_total: 0,
      organizations_count: 0,
    },
  },
  {
    id: 'demo-pc-3',
    name: 'PC1',
    location: null,
    lead_accountant: {
      id: 'demo-pc-3',
      name: 'ليان',
      email: 'layan@demo.com',
      annual_salary: '96000.00',
    },
    assistants: [],
    metrics: {
      cost: '8000.00',
      subscription_revenue_monthly: '4200.00',
      avg_invoices_upper_bound: 12,
      tax_registration_count: 1,
      sectors: {},
      org_types: { establishment: 1 },
      overdue_collection_total: '0.00',
      organizations_count: 1,
    },
  },
];

export const getDemoProfitCenters = (month?: string, location?: 'EGY' | 'SYR') => {
  const selected = month && /^\d{4}-\d{2}$/.test(month) ? month : demoYearMonth();
  const current = demoYearMonth();
  const previous = shiftDemoMonth(current, -1);
  const source = selected === current ? 'live' : selected === previous ? 'snapshot' : 'none';
  if (source === 'none') {
    return { month: selected, source, total: 0, profit_centers: [] };
  }
  const profit_centers = demoProfitCenters()
    .filter((center) => !location || center.location === location)
    .map((center) => ({
      ...center,
      month: selected,
      source,
      events: profitAssignments()
        .filter((row) => row.accountant_id === center.id)
        .flatMap((row) => eventsInMonth(row, selected))
        .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0)),
    }));
  const ordered = [
    ...profit_centers.filter((center) => center.location === 'EGY'),
    ...profit_centers.filter((center) => center.location === 'SYR'),
    ...profit_centers.filter((center) => center.location == null),
  ];
  return { month: selected, source, total: ordered.length, profit_centers: ordered };
};

export const updateDemoProfitCenterAssignment = (id: string, body: Record<string, unknown>) => {
  const existing = profitAssignments().find((row) => row.id === id);
  if (!existing) throw { response: { status: 404, data: { detail: 'Assignment not found' } } };
  const next = { ...existing };
  if ('started_at' in body) {
    const startedAt = String(body.started_at ?? '');
    if (next.ended_at && startedAt > next.ended_at) {
      throw { response: { status: 400, data: { detail: 'End time cannot be before the start time' } } };
    }
    next.started_at = startedAt;
  }
  if ('ended_at' in body) {
    const endedAt = body.ended_at == null || body.ended_at === '' ? null : String(body.ended_at);
    if (endedAt && endedAt < next.started_at) {
      throw { response: { status: 400, data: { detail: 'End time cannot be before the start time' } } };
    }
    next.ended_at = endedAt;
  }
  demoProfitAssignments = profitAssignments().map((row) => (row.id === id ? next : row));
  return next;
};

export const buildFinancialSummary = (dateAfter: string, dateBefore: string) => {
  const months = ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06'];
  const periods = months.map((period, i) => {
    const revenue = 120000 + i * 14000 + (i % 2 === 0 ? 9000 : -4000);
    const expenses = 70000 + i * 6000;
    return { period, revenue, expenses, net: revenue - expenses };
  });
  return {
    organization_id: DEMO_ORG_ID,
    date_after: dateAfter,
    date_before: dateBefore,
    currency: 'SAR',
    group_by: 'month',
    total_revenue: periods.reduce((s, p) => s + p.revenue, 0),
    total_expenses: periods.reduce((s, p) => s + p.expenses, 0),
    net_profit: periods.reduce((s, p) => s + p.net, 0),
    periods,
    fetched_at: iso(now()),
  };
};

// ── Wafeq live reports (tree) ──────────────────────────────────────────────────
const demoWafeqColumns = (months: string[]) => [
  ...months.map((m) => {
    const [y, mo] = m.split('-');
    const labels: Record<string, string> = {
      '01': 'يناير', '02': 'فبراير', '03': 'مارس', '04': 'أبريل', '05': 'مايو', '06': 'يونيو',
    };
    return {
      id: m,
      label: `${labels[mo] ?? mo} ${y}`,
      metadata: { from_date: `${m}-01`, to_date: `${m}-28` },
    };
  }),
  { id: 'row_totals', label: 'الإجمالي', metadata: {} },
];

export const buildDemoWafeqProfitAndLoss = (dateAfter: string, dateBefore: string) => {
  const months = ['2026-01', '2026-02', '2026-03'];
  const cols = demoWafeqColumns(months);
  const revenues = [50000, 55000, 60000];
  const cogs = [-20000, -22000, -24000];
  const opex = [-15000, -16000, -17000];
  const net = revenues.map((r, i) => r + cogs[i] + opex[i]);
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

  return {
    organization_id: DEMO_ORG_ID,
    report_type: 'profit_and_loss',
    params: { date_after: dateAfter, date_before: dateBefore, currency: 'SAR', group_by: 'month' },
    overview: { id: 'profit_and_loss', label: 'قائمة الدخل', currency: 'SAR', group_by: 'month' },
    columns: cols,
    rows: [
      {
        id: 'INCOME',
        group: 'INCOME',
        label: 'الإيرادات',
        metadata: {},
        summary: { id: 'summary_income', label: 'إجمالي الإيرادات', sub_totals: [...revenues, sum(revenues)] },
        children: [
          {
            id: 'acc_sales',
            label: '4100 المبيعات',
            metadata: { accounts: ['acc_sales'] },
            children: [],
            values: [...revenues.map((v) => v - 5000), sum(revenues) - 15000],
            summary: { id: 's1', label: 'إجمالي المبيعات', sub_totals: [...revenues.map((v) => v - 5000), sum(revenues) - 15000] },
          },
        ],
      },
      {
        id: 'COGS',
        group: 'COGS',
        label: 'تكلفة المبيعات',
        summary: { id: 'summary_cogs', label: 'إجمالي تكلفة المبيعات', sub_totals: [...cogs, sum(cogs)] },
        children: [],
      },
      {
        id: 'OPERATING_EXPENSE',
        group: 'OPERATING_EXPENSE',
        label: 'المصروفات التشغيلية',
        summary: { id: 'summary_opex', label: 'إجمالي المصروفات', sub_totals: [...opex, sum(opex)] },
        children: [],
      },
      {
        id: 'NET_PROFIT',
        group: 'NET_PROFIT',
        label: 'صافي الربح',
        summary: { id: 'summary_net', label: 'صافي الربح', sub_totals: [...net, sum(net)] },
        children: [],
      },
    ],
    fetched_at: iso(now()),
  };
};

export const buildDemoWafeqBalanceSheet = (date: string) => ({
  organization_id: DEMO_ORG_ID,
  report_type: 'balance_sheet',
  params: { date, period_count: 1, currency: 'SAR', group_by: 'month' },
  overview: { id: 'balance_sheet', label: 'قائمة المركز المالي', currency: 'SAR', date, period_count: 1 },
  columns: [
    { id: '2026-05-31', label: '31 مايو, 2026', metadata: { date: '2026-05-31' } },
    { id: '2026-06-30', label: '30 يونيو, 2026', metadata: { date: '2026-06-30' } },
    { id: 'row_totals', label: 'الإجمالي', metadata: {} },
  ],
  rows: [
    {
      id: 'ASSET',
      group: 'ASSET',
      label: 'الأصول',
      summary: { id: 's_asset', label: 'إجمالي الأصول', sub_totals: [500000, 520000, 1020000] },
      children: [
        {
          id: 'CURRENT_ASSET',
          group: 'CURRENT_ASSET',
          label: 'الأصول المتداولة',
          summary: { id: 's_ca', label: 'إجمالي الأصول المتداولة', sub_totals: [150000, 162000, 312000] },
          children: [
            {
              id: 'acc_cash',
              label: '1111 الخزينة',
              children: [],
              values: [50000, 55000, 105000],
              summary: { id: 's_cash', label: 'إجمالي الخزينة', sub_totals: [50000, 55000, 105000] },
            },
          ],
        },
      ],
    },
    {
      id: 'EQUITY',
      group: 'EQUITY',
      label: 'حقوق الملكية',
      summary: { id: 's_eq', label: 'إجمالي حقوق الملكية', sub_totals: [300000, 310000, 610000] },
      children: [],
    },
  ],
  fetched_at: iso(now()),
});

export const buildDemoWafeqCashFlow = (dateAfter: string, dateBefore: string) => {
  const months = ['2026-01', '2026-02', '2026-03'];
  const cols = demoWafeqColumns(months);
  const changes = [12000, 15000, 8000];
  const sum = changes.reduce((a, b) => a + b, 0);
  return {
    organization_id: DEMO_ORG_ID,
    report_type: 'cash_flow',
    params: { date_after: dateAfter, date_before: dateBefore, currency: 'SAR', group_by: 'month' },
    overview: { id: 'cash_flow', label: 'التدفقات النقدية', currency: 'SAR', group_by: 'month' },
    columns: cols,
    rows: [
      {
        id: 'OPERATING',
        group: 'OPERATING',
        label: 'الأنشطة التشغيلية',
        summary: { id: 's_op', label: 'إجمالي التشغيل', sub_totals: [...changes, sum] },
        children: [],
      },
      {
        id: 'NET_CASH_CHANGE',
        group: 'NET_CASH_CHANGE',
        label: 'صافي التغير في النقد',
        summary: { id: 's_net', label: 'صافي التغير', sub_totals: [...changes, sum] },
        children: [],
      },
    ],
    fetched_at: iso(now()),
  };
};

// ── Financial view (period options + unified view) ───────────────────────────
const demoDateStr = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const buildDemoPeriodOptions = (organizationId: string) => {
  const today = now();
  const td = demoDateStr(today);
  const y = today.getFullYear();
  const m = today.getMonth();
  const preset = (key: string, label_ar: string, label_en: string, after: string, before: string, range_ar: string, range_en: string) => ({
    key, label_ar, label_en, date_after: after, date_before: before, display_range_ar: range_ar, display_range_en: range_en,
  });
  return {
    organization_id: organizationId,
    fiscal_year_start_month: 1,
    presets: [
      preset('last_3_months', 'آخر 3 أشهر', 'Last 3 months', demoDateStr(new Date(y, m - 3, 1)), td, 'آخر 3 أشهر', 'Last 3 months'),
      preset('last_6_months', 'آخر 6 أشهر', 'Last 6 months', demoDateStr(new Date(y, m - 6, 1)), td, 'آخر 6 أشهر', 'Last 6 months'),
      preset('last_12_months', 'آخر 12 شهر', 'Last 12 months', demoDateStr(new Date(y, m - 12, 1)), td, 'آخر 12 شهر', 'Last 12 months'),
      preset('current_month', 'الشهر الحالي', 'Current month', demoDateStr(new Date(y, m, 1)), demoDateStr(new Date(y, m + 1, 0)), 'الشهر الحالي', 'Current month'),
      preset('current_quarter', 'الربع الحالي', 'Current quarter', demoDateStr(new Date(y, Math.floor(m / 3) * 3, 1)), demoDateStr(new Date(y, Math.floor(m / 3) * 3 + 3, 0)), 'الربع الحالي', 'Current quarter'),
      preset('current_fiscal_year', 'السنة المالية الحالية', 'Current fiscal year', `${y}-01-01`, td, `يناير–${td}`, `Jan–${td}`),
      preset('two_fiscal_years', 'سنتان ماليتان', 'Two fiscal years', `${y - 1}-01-01`, td, `${y - 1}–${y}`, `${y - 1}–${y}`),
      preset('three_fiscal_years', '3 سنوات مالية', 'Three fiscal years', `${y - 2}-01-01`, td, `${y - 2}–${y}`, `${y - 2}–${y}`),
      preset('previous_month', 'الشهر السابق', 'Previous month', demoDateStr(new Date(y, m - 1, 1)), demoDateStr(new Date(y, m, 0)), 'الشهر السابق', 'Previous month'),
      preset('previous_quarter', 'الربع السابق', 'Previous quarter', demoDateStr(new Date(y, Math.floor(m / 3) * 3 - 3, 1)), demoDateStr(new Date(y, Math.floor(m / 3) * 3, 0)), 'الربع السابق', 'Previous quarter'),
      preset('previous_fiscal_year', 'السنة المالية السابقة', 'Previous fiscal year', `${y - 1}-01-01`, `${y - 1}-12-31`, String(y - 1), String(y - 1)),
      preset('previous_two_fiscal_years', 'السنتان الماليتان السابقتان', 'Previous two fiscal years', `${y - 2}-01-01`, `${y - 1}-12-31`, `${y - 2}–${y - 1}`, `${y - 2}–${y - 1}`),
      preset('custom', 'نطاق زمني مخصص', 'Custom date range', demoDateStr(new Date(y, m - 3, 1)), td, 'مخصص', 'Custom'),
    ],
  };
};

export const buildDemoFinancialView = (params: Record<string, string>) => {
  const reportType = params.report_type || 'profit_and_loss';
  const groupBy = params.group_by === 'year' ? 'year' : 'month';
  const preset = params.period_preset || 'current_fiscal_year';
  const dateAfter = params.date_after || `${now().getFullYear()}-01-01`;
  const dateBefore = params.date_before || demoDateStr(now());

  let wafeqData;
  if (reportType === 'balance_sheet') {
    wafeqData = buildDemoWafeqBalanceSheet(dateBefore);
  } else if (reportType === 'cash_flow') {
    wafeqData = buildDemoWafeqCashFlow(dateAfter, dateBefore);
  } else {
    wafeqData = buildDemoWafeqProfitAndLoss(dateAfter, dateBefore);
  }

  return {
    source: 'wafeq' as const,
    report_type: reportType,
    group_by: groupBy,
    period_preset: preset,
    date_after: dateAfter,
    date_before: dateBefore,
    display_range_ar: wafeqData.overview.label,
    display_range_en: wafeqData.overview.label,
    columns: wafeqData.columns,
    rows: wafeqData.rows,
    overview: wafeqData.overview,
    periods: [],
    reports: [],
    fetched_at: iso(now()),
  };
};

// ── Notifications ──────────────────────────────────────────────────────────────
export const demoNotifications = {
  result_list: [
    {
      id: nextId('notif'),
      title_en: 'New report ready',
      title_ar: 'تقرير جديد جاهز',
      message_en: 'Your March income statement is ready for review.',
      message_ar: 'قائمة الدخل لشهر مارس جاهزة للمراجعة.',
      type: 'in_app',
      data: {},
      is_read: false,
      created_at: iso(minsAgo(50)),
      organization_id: DEMO_ORG_ID,
      organization_name: 'مؤسسة الأفق التجارية',
    },
    {
      id: nextId('notif'),
      title_en: 'Document received',
      title_ar: 'تم استلام مستند',
      message_en: 'Your accountant received the uploaded invoices.',
      message_ar: 'استلم محاسبك الفواتير التي رفعتها.',
      type: 'in_app',
      data: {},
      is_read: true,
      created_at: iso(daysAgo(1)),
      organization_id: DEMO_ORG_ID,
      organization_name: 'مؤسسة الأفق التجارية',
    },
  ],
  total_count: 2,
};

// ── Accountants ────────────────────────────────────────────────────────────────
export const demoAccountants = {
  accountants: [
    { id: ACCOUNTANT_ID, name: ACCOUNTANT_NAME, email: 'ahmed@mohasby.com', google_calendar_connected: true },
  ],
};

// ── Meeting credits ──────────────────────────────────────────────────────────────
export const demoMeetingCredits = {
  monthly_limit: 4,
  monthly_used: 1,
  monthly_remaining: 3,
  weekly_base_limit: 1,
  weekly_bonus: 0,
  weekly_limit: 1,
  weekly_used: 0,
  weekly_remaining: 1,
};

// ── Chatbot welcome ──────────────────────────────────────────────────────────────
export const demoChatbotWelcome = {
  node_id: 'root',
  title: 'كيف نقدر نساعدك؟',
  message: 'اختر من الخيارات التالية أو اكتب رسالتك مباشرة لمحاسبك.',
  is_tree_mode: false,
  show_back_to_main: false,
  options: [],
};

// ── Monthly closure ───────────────────────────────────────────────────────────
const demoClosureStore: Record<string, MonthClosureItem[]> = {};

function buildDemoClosureMonths(): MonthClosureItem[] {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    let status: MonthClosureStatus = 'PENDING';
    if (month <= 8) status = 'CLOSED';
    else if (month <= 11) status = 'IN_PROGRESS';
    return {
      month,
      status,
      updated_at: status !== 'PENDING' ? iso(now()) : null,
      updated_by: status !== 'PENDING' ? { id: DEMO_USER_ID, name: 'أحمد المحاسب' } : null,
    };
  });
}

export function getDemoMonthlyClosure(orgId: string, year: number): MonthlyClosureResponse {
  const key = `${orgId}:${year}`;
  if (!demoClosureStore[key]) {
    demoClosureStore[key] = buildDemoClosureMonths();
  }
  return { organization_id: orgId, year, months: [...demoClosureStore[key]] };
}

export function getDemoClientProjectProgress(_orgId: string): ClientProjectProgressResponse {
  const projects = [
    {
      id: 'demo-project-setup',
      title: 'إعداد النظام المحاسبي',
      status: 'in_progress' as const,
      progress_done: 3,
      progress_total: 8,
      progress_percent: 38,
    },
    {
      id: 'demo-project-jan',
      title: 'إقفال شهر يناير',
      status: 'done' as const,
      progress_done: 5,
      progress_total: 5,
      progress_percent: 100,
    },
  ];
  return { projects, total: projects.length };
}

export function updateDemoMonthClosure(
  orgId: string,
  year: number,
  month: number,
  status: MonthClosureStatus,
): MonthlyClosureResponse {
  const data = getDemoMonthlyClosure(orgId, year);
  if (status === 'CLOSED' && month > 1) {
    const prev = data.months.find((m) => m.month === month - 1);
    if (prev && prev.status !== 'CLOSED') {
      const err: any = new Error('sequential');
      err.response = { data: { detail: 'Cannot close this month before the previous month is closed' } };
      throw err;
    }
  }
  const updated = data.months.map((m) =>
    m.month === month
      ? {
          ...m,
          status,
          updated_at: iso(now()),
          updated_by: { id: DEMO_USER_ID, name: 'أحمد المحاسب' },
        }
      : m,
  );
  demoClosureStore[`${orgId}:${year}`] = updated;
  return { organization_id: orgId, year, months: [...updated] };
}

// ── User profile (settings) ───────────────────────────────────────────────────
let demoUserProfile: User = buildDemoUser();

export function getDemoUserProfile(): User {
  return { ...demoUserProfile, data: { ...demoUserProfile.data, access: [...demoUserProfile.data.access] } };
}

export function updateDemoUserProfile(data: {
  name?: string;
  email?: string;
  phone?: string;
  annual_salary?: number | null;
}): User {
  demoUserProfile = {
    ...demoUserProfile,
    name: data.name ?? demoUserProfile.name,
    email: data.email ?? demoUserProfile.email,
    phone: data.phone ?? demoUserProfile.phone,
    data: {
      ...demoUserProfile.data,
      phone: data.phone ?? demoUserProfile.data.phone,
      ...(data.annual_salary !== undefined ? { annual_salary: data.annual_salary } : {}),
    },
  };
  return getDemoUserProfile();
}

// ── Contract template (admin demo) ────────────────────────────────────────────

const DEMO_TEMPLATE_HTML = `<!DOCTYPE html><html><body><h1>اتفاقية اشتراك الخدمة</h1><p>{{ organization_name }} — {{ signer_name }}</p><p>{{ total_fee }}</p></body></html>`;

let demoContractTemplateVersions = [
  {
    id: 'demo-template-v1',
    version: 'v1.0',
    name: 'Default template',
    html_body: DEMO_TEMPLATE_HTML,
    is_active: true,
    notes: null as string | null,
    created_by_user_id: DEMO_USER_ID,
    created_at: iso(daysAgo(30)),
    updated_at: iso(daysAgo(30)),
  },
];

export function getDemoActiveContractTemplate() {
  return demoContractTemplateVersions.find((v) => v.is_active) ?? demoContractTemplateVersions[0];
}

export function getDemoContractTemplateVersions() {
  return demoContractTemplateVersions.map(({ html_body: _html, created_by_user_id: _uid, ...rest }) => rest);
}

export function getDemoContractTemplateByVersion(version: string) {
  return demoContractTemplateVersions.find((v) => v.version === version) ?? null;
}

export function saveDemoContractTemplate(data: { html_body: string; name?: string; notes?: string }) {
  demoContractTemplateVersions = demoContractTemplateVersions.map((v) => ({ ...v, is_active: false }));
  const nextNum = demoContractTemplateVersions.length + 1;
  const created = {
    id: `demo-template-v${nextNum}`,
    version: `v${nextNum}.0`,
    name: data.name || `Version ${nextNum}`,
    html_body: data.html_body,
    is_active: true,
    notes: data.notes ?? null,
    created_by_user_id: DEMO_USER_ID,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  demoContractTemplateVersions = [created, ...demoContractTemplateVersions];
  return created;
}

export function activateDemoContractTemplateVersion(version: string) {
  const target = demoContractTemplateVersions.find((v) => v.version === version);
  if (!target) return null;
  demoContractTemplateVersions = demoContractTemplateVersions.map((v) => ({
    ...v,
    is_active: v.version === version,
    updated_at: v.version === version ? new Date().toISOString() : v.updated_at,
  }));
  return demoContractTemplateVersions.find((v) => v.version === version) ?? null;
}

export function resetDemoContractTemplate() {
  return saveDemoContractTemplate({ html_body: DEMO_TEMPLATE_HTML, name: 'System default' });
}

export function deleteDemoContractTemplateVersion(version: string): { ok: true } | { ok: false; status: number } {
  const target = demoContractTemplateVersions.find((v) => v.version === version);
  if (!target) return { ok: false, status: 404 };
  if (target.is_active) return { ok: false, status: 400 };
  demoContractTemplateVersions = demoContractTemplateVersions.filter((v) => v.version !== version);
  return { ok: true };
}

// ── Response time monitoring ─────────────────────────────────────────────────

const demoResponseTimeWaitingItems: ResponseTimeItem[] = [
  {
    organization_id: DEMO_ORG_ID,
    organization_name: demoOrganization.name,
    accountant_id: 'acct-demo-1',
    accountant_name: 'سارة المحاسبة',
    opened_at: chatTime(minsAgo(1650)),
    closed_at: null,
    waiting_hours: 27.5,
    status: 'breached',
    sla_breached: true,
  },
  {
    organization_id: 'org-demo-3',
    organization_name: 'شركة الخليج',
    accountant_id: 'acct-demo-2',
    accountant_name: 'أحمد المحاسب',
    opened_at: chatTime(minsAgo(3000)),
    closed_at: null,
    waiting_hours: 50,
    status: 'breached',
    sla_breached: true,
  },
  {
    organization_id: 'org-demo-2',
    organization_name: 'مؤسسة النور',
    accountant_id: 'acct-demo-1',
    accountant_name: 'سارة المحاسبة',
    opened_at: chatTime(minsAgo(480)),
    closed_at: null,
    waiting_hours: 8,
    status: 'open',
    sla_breached: false,
  },
];

/** Closed cycles — summary cards only, never returned as table rows. */
const demoResponseTimeClosedForAvg = [
  { accountant_id: 'acct-demo-2', waiting_hours: 18 },
  { accountant_id: 'acct-demo-1', waiting_hours: 6 },
  { accountant_id: 'acct-demo-2', waiting_hours: 22 },
];

export function getDemoResponseTime(params: Record<string, unknown> = {}): ResponseTimeResponse {
  const status = String(params.status ?? 'open');
  if (status === 'closed') {
    throw new Error('status=closed is not supported');
  }

  const accountantId = params.accountant_id ? String(params.accountant_id) : '';
  const limit = Math.min(100, Math.max(1, Number(params.limit) || 50));
  const offset = Math.max(0, Number(params.offset) || 0);

  let filtered = status === 'breached'
    ? demoResponseTimeWaitingItems.filter((item) => item.sla_breached)
    : [...demoResponseTimeWaitingItems];

  if (accountantId) {
    filtered = filtered.filter((item) => item.accountant_id === accountantId);
  }

  const sortBy = String(params.sort_by || 'waiting_hours');
  const sortDir = String(params.sort_dir || 'desc');
  filtered.sort((a, b) => {
    const cmp = sortBy === 'organization_name'
      ? a.organization_name.localeCompare(b.organization_name, 'ar')
      : a.waiting_hours - b.waiting_hours;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const closedForAvg = accountantId
    ? demoResponseTimeClosedForAvg.filter((item) => item.accountant_id === accountantId)
    : demoResponseTimeClosedForAvg;
  const avgHours = closedForAvg.length
    ? closedForAvg.reduce((sum, item) => sum + item.waiting_hours, 0) / closedForAvg.length
    : null;
  const within24 = closedForAvg.filter((item) => item.waiting_hours <= 24).length;
  const pctWithin24h = closedForAvg.length ? (within24 / closedForAvg.length) * 100 : null;

  const waitingPool = accountantId
    ? demoResponseTimeWaitingItems.filter((item) => item.accountant_id === accountantId)
    : demoResponseTimeWaitingItems;
  const openCount = waitingPool.length;
  const breachedCount = waitingPool.filter((item) => item.sla_breached).length;

  return {
    summary: {
      avg_hours: avgHours != null ? Math.round(avgHours * 10) / 10 : null,
      pct_within_24h: pctWithin24h != null ? Math.round(pctWithin24h * 10) / 10 : null,
      closed_count: closedForAvg.length,
      open_count: openCount,
      breached_count: breachedCount,
      target_hours: 24,
    },
    items: filtered.slice(offset, offset + limit),
    total_count: filtered.length,
  };
}
